import {Component, Input, OnInit} from '@angular/core';
import {
  ConfigurationState,
  GeneralOptionsGroupName,
  InteriorOptions,
  InventoryService,
  PostingDataResponseModel,
  VehicleOption,
  EditPostingState,
  UpdatePosting,
  EditPostingFormNames
} from 'remarketing-angular-library';
import { Store } from '@ngxs/store';
import {OptionsAndAccessoriesFormsService} from '../../services/options-and-accessories-forms.service';
import { cloneDeep as _cloneDeep } from 'lodash';
import {SubGroupName} from '../../data/option-sub-group-mapping';
import { takeUntil } from 'rxjs/operators';
import { PostingFormDirective } from '../posting-form/posting-form.directive';
import {PostingAnalyticsHelperService} from '../../services/posting-analytics-helper.service';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  OptionsAndAccessoriesFormComponent
} from '../options-and-accessories-form/options-and-accessories-form.component';

@Component({
    selector: 'app-interior-form',
    templateUrl: './interior-form.component.html',
    styleUrls: ['./interior-form.component.scss'],
    imports: [TranslocoDirective, OptionsAndAccessoriesFormComponent]
})
export class InteriorFormComponent extends PostingFormDirective implements OnInit {
  postingData: PostingDataResponseModel;
  originalOptions: VehicleOption[];
  otherOptions: VehicleOption[];
  additionalOptions: VehicleOption[];
  showAllOptions = false;
  @Input() postingTypeString: string;

  constructor(
    private readonly store: Store,
    private readonly inventoryService: InventoryService,
    private readonly optionsAndAccessoriesFormsService: OptionsAndAccessoriesFormsService,
    private readonly postingAnalyticsHelperService: PostingAnalyticsHelperService
  ) {
    super();
  }

  ngOnInit() {
    this.store
      .select(EditPostingState.updatedPosting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(postingData => {
        this.postingData = _cloneDeep(postingData);
      });

    // Store our original options so that they're always an available choice, regardless of if they come from the additional list
    const originalPosting = _cloneDeep(this.store.selectSnapshot(EditPostingState.originalPosting));

    // Set up our initial options, for when Show All Options isn't checked
    this.originalOptions = Object.keys(originalPosting.inventoryItem.vehicleOptions.interiorOptions).reduce(
      (acc, currVal) => {
        return acc.concat(originalPosting.inventoryItem.vehicleOptions.interiorOptions[currVal]);
      },
      []
    );
    this.setInitialOptions();

    this.inventoryService.getVehicleGeneralOptions(GeneralOptionsGroupName.INTERIOR).subscribe(x => {
      // Pull down Additional Options and select the Show All Options checkbox if we have any installed options from that list
      this.additionalOptions = x;
      if (this.otherOptions.filter(o => o.fromAllOptionsList).length > 0) {
        this.toggleAllOptions();
      }
    });
    this.sendPageloadAnalyticsEvent();
  }

  sendPageloadAnalyticsEvent() {
    this.postingAnalyticsHelperService.sendPageloadAnalyticsEvent(
      this.store.selectSnapshot(ConfigurationState.configuration).tenantName,
      this.postingTypeString,
      EditPostingFormNames.OPTIONS_INTERIOR
    );
  }

  // Updates the vehicle's interiorOptions arrays and persists the update to the store
  installOptions(options: VehicleOption[]) {
    // Set our posting data interior options based on the subGroupName of the selected options
    const interiorOptions = this.postingData.inventoryItem.vehicleOptions.interiorOptions;
    const postedOptions = [
      interiorOptions.airBags,
      interiorOptions.powerLocks,
      interiorOptions.audioOptions,
      interiorOptions.otherOptions,
      interiorOptions.powerDoors,
      interiorOptions.seatingOptions,
      interiorOptions.powerWindows
    ];

    interiorOptions.airBags = [];
    interiorOptions.audioOptions = [];
    interiorOptions.otherOptions = [];
    interiorOptions.powerDoors = [];
    interiorOptions.powerLocks = [];
    interiorOptions.powerWindows = [];
    interiorOptions.seatingOptions = [];

    options.forEach(o => {
      switch (o.subGroupName) {
        case SubGroupName.INTERIOR_AIR_BAGS:
          interiorOptions.airBags.push(o);
          break;
        case SubGroupName.INTERIOR_AUDIO:
          interiorOptions.audioOptions.push(o);
          break;
        case SubGroupName.INTERIOR_POWER_DOORS:
          interiorOptions.powerDoors.push(o);
          break;
        case SubGroupName.INTERIOR_POWER_LOCKS:
          interiorOptions.powerLocks.push(o);
          break;
        case SubGroupName.INTERIOR_POWER_WINDOWS:
          interiorOptions.powerWindows.push(o);
          break;
        case SubGroupName.INTERIOR_SEATING:
          interiorOptions.seatingOptions.push(o);
          break;
        default:
          interiorOptions.otherOptions.push(o);
          break;
      }
    });

    this.updateEmptyOptions(interiorOptions, postedOptions);

    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  // interiorOptions: selected options, can not contain an empty list
  // postedOptions: list of the selections from the postingData
  // If an option is not defined in the interior options, replace with the value from the postingData.
  //  the postingData contains the correct option structure instead of an empty list
  updateEmptyOptions(interiorOptions: InteriorOptions, postedOptions: VehicleOption[][]) {
    const airBags = postedOptions[0];
    const powerLocks = postedOptions[1];
    const audioOptions = postedOptions[2];
    const otherOptions = postedOptions[3];
    const powerDoors = postedOptions[4];
    const seatingOptions = postedOptions[5];
    const powerWindows = postedOptions[6];
    if (!interiorOptions.airBags.length) {
      interiorOptions.airBags = airBags;
    }
    if (!interiorOptions.audioOptions.length) {
      interiorOptions.audioOptions = audioOptions;
    }
    if (!interiorOptions.otherOptions.length) {
      interiorOptions.otherOptions = otherOptions;
    }
    if (!interiorOptions.powerDoors.length) {
      interiorOptions.powerDoors = powerDoors;
    }
    if (!interiorOptions.powerLocks.length) {
      interiorOptions.powerLocks = powerLocks;
    }
    if (!interiorOptions.powerWindows.length) {
      interiorOptions.powerWindows = powerWindows;
    }
    if (!interiorOptions.seatingOptions.length) {
      interiorOptions.seatingOptions = seatingOptions;
    }
  }

  removeOption(option: VehicleOption) {
    if (!this.showAllOptions && option.fromAllOptionsList) {
      this.otherOptions = this.otherOptions.filter(other => other.optionCode !== option.optionCode);
    }
  }

  // Handles showing/hiding additional options
  toggleAllOptions() {
    this.showAllOptions = !this.showAllOptions;

    if (this.showAllOptions) {
      // Show list of additional options and clean up any duplicates
      this.otherOptions = this.otherOptions.concat(this.additionalOptions);
      this.otherOptions = this.optionsAndAccessoriesFormsService.cleanOptionsList(this.otherOptions);
    } else {
      this.otherOptions = this.otherOptions.filter(o => o.installed || o.fromAllOptionsList === false);
      this.store.dispatch(new UpdatePosting(this.postingData));
      this.setInitialOptions();
    }
  }

  setInitialOptions() {
    const options = Object.keys(this.postingData.inventoryItem.vehicleOptions.interiorOptions).reduce(
      (acc, currVal) => {
        return acc.concat(this.postingData.inventoryItem.vehicleOptions.interiorOptions[currVal]);
      },
      []
    );
    this.otherOptions = this.optionsAndAccessoriesFormsService.getInitialOptions(options, this.originalOptions);
  }
}
